import { useState, useEffect } from "react";
import { MessageType } from "../../../utilis/types/Types";

type UseAudioFilesProps = {
    greetingsAudioUrl: any;
    menuAudioUrl: any;
    setValue: any;
};

export const useAudioFiles = ({ greetingsAudioUrl, menuAudioUrl,setValue }: UseAudioFilesProps) => {
    const [audioFiles, setAudioFiles] = useState<any>({});

    useEffect(() => {
        const updatedAudioFiles = { ...audioFiles };
        if (greetingsAudioUrl) {
            updatedAudioFiles.WELCOME_MESSAGE = greetingsAudioUrl.data;
        }
        if (menuAudioUrl) {
            updatedAudioFiles.MENU_MESSAGE = menuAudioUrl.data;
        }
        setAudioFiles(updatedAudioFiles);
    }, [greetingsAudioUrl, menuAudioUrl]);

    const setSavedTextToSpeech = (tts: TtsSettings | undefined, pendingUpload: string) => {
        if (!tts) return;
        if (pendingUpload === MessageType.MENU_MESSAGE) {
            setValue('ivrMenuOption.messageSettings.MENU_MESSAGE.tts', tts);
        }
        if (pendingUpload === MessageType.WELCOME_MESSAGE) {
            setValue('ivrMenuOption.messageSettings.WELCOME_MESSAGE.tts', tts);
        }
        if (pendingUpload.startsWith(MessageType.PLAY_MESSAGE)) {
            const index = pendingUpload.split("_")[2];
            setValue(`ivrMenuOption.actions.${index}.messageSettings.tts`, tts);
        }
    }

    const updateAudioFiles = (currentAudioFiles: any, fileData: any, pendingUpload: string) => {
        if (!pendingUpload) return currentAudioFiles;

        const updatedAudioFiles = { ...currentAudioFiles };
        const audioUrl = fileData?.audioURL || fileData?.data;

        if (!fileData) return updatedAudioFiles;

        if (pendingUpload === MessageType.MENU_MESSAGE) {
            updatedAudioFiles.MENU_MESSAGE = audioUrl;
            setValue('ivrMenuOption.messageSettings.MENU_MESSAGE.originalFileName', fileData?.originalFileName);
            setValue('ivrMenuOption.messageSettings.MENU_MESSAGE.bucketFileName', fileData?.bucketFileName);
        } else if (pendingUpload === MessageType.WELCOME_MESSAGE) {
            updatedAudioFiles.WELCOME_MESSAGE = audioUrl;
            setValue('ivrMenuOption.messageSettings.WELCOME_MESSAGE.originalFileName', fileData?.originalFileName);
            setValue('ivrMenuOption.messageSettings.WELCOME_MESSAGE.bucketFileName', fileData?.bucketFileName);

        } else if (pendingUpload.startsWith(MessageType.PLAY_MESSAGE)) {
            const index = pendingUpload.split("_")[2];
            updatedAudioFiles.PLAY_MESSAGE = updatedAudioFiles.PLAY_MESSAGE || {};
            updatedAudioFiles.PLAY_MESSAGE[index] = audioUrl;
            setValue(`ivrMenuOption.actions.${index}.messageSettings.bucketFileName`, fileData?.bucketFileName);
            setValue(`ivrMenuOption.actions.${index}.messageSettings.originalFileName`, fileData?.originalFileName);
            setValue(`ivrMenuOption.actions.${index}.label`, (<div className={'flex items-center gap-2'}>
                <span>
                    <img className={'w-5 h-5'} src={audioImg}/>
                </span>
                Play an audio and back to main menu
            </div>));
            setValue(`ivrMenuOption.actions.${index}.type`, 'PlayMessage');
        }
        setAudioFiles(updatedAudioFiles);
        return updatedAudioFiles;
    };

    const removeAudioFile = (type: string) => {
        setAudioFiles((prev: any) => ({
            ...prev,
            [type]: null,
        }));
    };

    return {
        audioFiles,
        setAudioFiles,
        updateAudioFiles,
        removeAudioFile,
        setSavedTextToSpeech,
    };
};

import { useMemo } from "react";
import { useFetchMsisdnsAdminQuery } from "../../../store/features/MsisdnSettings/msisdnSettingsService";
import { useGetUsersQuery} from "../../../store/features/Users/userService";
import { useFetchGroupsAdminQuery } from "../../../store/features/groups/groupsService";
import { Colors } from "../../../utilis/types/Types";
import {PhoneHQAvatar, PhoneHQIconAvatar} from 'ch3-ui-lib';
import { FlagImage } from "react-international-phone";
import { parsePhoneNumber } from "libphonenumber-js/max";
import { getCallingCountries } from "../../../components/Dialer/services/countryDataService";
import { getFormatedPhoneNumber } from "../../../utilis/msisdnUtilis";
import audioImg from "../../../assets/audio.svg";
import type {TtsSettings} from "../../../components/MessageDialog/MessageDialog";

type UseMsisdnAndUsersProps = {
    userId: string | null;
    phrase?: string;
};

export const useMsisdnAndUsers = ({ userId, phrase = '' }: UseMsisdnAndUsersProps) => {
    const [groupsQuery, setGroupsQuery] = useState<any>({});

    const { data: adminGroupsData, isLoading: groupsLoading } = useFetchGroupsAdminQuery({ userId, phrase ,...groupsQuery });
    const { data: usersData, isLoading: userLoading } = useGetUsersQuery();
    const { data: phoneNumberData, isLoading: phoneNumbersLoading } = useFetchMsisdnsAdminQuery({ phrase });

    const getPhoneNumber = (val: string) => {
        const parsedPhoneNumber = parsePhoneNumber(`+${val}`);
        const country = getCallingCountries().find(
            (country) => country.alpha2 === parsedPhoneNumber?.country
        );
        return (
            <div className={"flex items-center gap-2"}>
        <span>
          <FlagImage size={30} iso2={country?.alpha2.toLowerCase()} className={"px-2"} />
        </span>
                {getFormatedPhoneNumber(val)}
            </div>
        );
    };

    const phoneNumbersItems = useMemo(
        () =>
            phoneNumberData?.data.map((phoneNumber) => ({
                label: phoneNumber.label,
                value: phoneNumber.msisdnId.toString(),
                key: `${phoneNumber.msisdn}`,
                prefixElement: (
                    <div className="ml-2">
                        <PhoneHQIconAvatar icon={phoneNumber.icon} color={phoneNumber.iconColor ?? Colors.auto} name={phoneNumber.label} size="32px"/>
                    </div>
                ),
            })) || [],
        [phoneNumberData]
    );

    const userItems = useMemo(
        () =>
            usersData?.data.map((user: any) => ({
                label: `${user.firstName.toString()} ${user.lastName.toString()}`,
                value: user.userId.toString(),
                key: `user${user.userId}`,
                prefixElement: (
                    <div className="ml-2">
                        <PhoneHQAvatar name={`${user.firstName} ${user.lastName}`} size="32px"/>
                    </div>
                ),
            })) || [],
        [usersData]
    );

    const groupItems = useMemo(
        () =>
            adminGroupsData?.groups?.map((group: any) => ({
                label: group.group.name,
                value: group.group.groupId?.toString(),
                key: `group${group.group.groupId}`,
                prefixElement: (
                    <div>
                        <PhoneHQIconAvatar icon={group?.group.icon} color={group.group.iconColor} name={group.group.name} size="32px"/>
                    </div>
                ),
            })) || [],
        [adminGroupsData]
    );

    const onGroupEnd = () => {
        const lastGroup = adminGroupsData?.groups?.at(-1);

        setGroupsQuery({
            lastName: lastGroup?.group.name ,
            lastGroupId: lastGroup?.group.groupId
        });
    }

    const isLoading = groupsLoading || userLoading || phoneNumbersLoading;

    return {
        phoneNumbersItems,
        userItems,
        groupItems,
        getPhoneNumber,
        isLoading,
        onGroupEnd,
    };
};